import { api } from '@packages/backend/convex/_generated/api';
import {
  getUserErrorMessage,
  type StandalonePrinterCvx,
  standalonePrinterCvxSchema,
  standalonePrinterSchema,
} from '@packages/shared';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { format, formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import { toast } from 'sonner';
import { ActionDropdown } from '@/components/ActionDropdown';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { ActionButton } from '@/components/ui+/ActionButton';
import { ColorBadge } from '@/components/ui+/ColorBadge';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { StandalonePrinterForm } from './components/StandalonePrinterForm';

const StandalonePrintersPage = () => {
  const convex = useConvex();
  const router = useRouter();
  const { data, error, isError, isPending } = useQuery(api.standalonePrinters.list);

  const [isNew, setIsNew] = useState<boolean>(false);
  const [editStandalonePrinter, setEditStandalonePrinter] = useState<
    StandalonePrinterCvx | undefined
  >(undefined);

  const handleDelete = async (id: string) => {
    try {
      await convex.mutation(api.standalonePrinters.destroy, { id });
      toast.success('Deleted successfully');
    } catch (error) {
      toast.error("Couldn't delete", {
        description: getUserErrorMessage(error),
      });
    }
  };

  const handleCreateNew = () => {
    setIsNew(true);
  };

  if (isPending) return <LoadingIcon />;
  if (isError) {
    return <div>Error loading standalone printers. {error.message}</div>;
  }

  return (
    <>
      {isNew && <StandalonePrinterForm onClose={() => setIsNew(false)} />}
      {editStandalonePrinter && (
        <StandalonePrinterForm
          onClose={() => setEditStandalonePrinter(undefined)}
          standalonePrinter={standalonePrinterSchema.decode(editStandalonePrinter)}
          standalonePrinterId={editStandalonePrinter._id}
        />
      )}
      <Card>
        <CardHeader>
          <CardTitle>Standalone Printers</CardTitle>
          <CardAction>
            <ActionButton onClick={handleCreateNew}>Create New</ActionButton>
          </CardAction>
        </CardHeader>
        <CardContent>
          <Table>
            <TableBody>
              {data.map((p) => {
                return (
                  <TableRow key={p.imei}>
                    <TableCell width={90}>
                      <ColorBadge color={p.isOnlineNow ? 'green' : 'gray'}>
                        {p.isOnlineNow ? 'ONLINE' : 'OFFLINE'}
                      </ColorBadge>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{p.restaurantName}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{p.imei}</div>
                      <div className="text-sm text-muted-foreground">{p.serialNumber}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {formatDistanceToNow(p.updatedAt, { addSuffix: true })}
                      </div>
                      <div className="text-muted-foreground">
                        {format(p.updatedAt, 'h:mm a d-MMM-yy')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <ActionDropdown
                        onDelete={() => handleDelete(p._id)}
                        onEdit={() =>
                          setEditStandalonePrinter(standalonePrinterCvxSchema.decode(p))
                        }
                      >
                        <DropdownMenuItem
                          onSelect={() =>
                            router.navigate({
                              to: '/a/standalone-printers/$imei/emulator',
                              params: { imei: p.imei },
                            })
                          }
                        >
                          View emulator
                        </DropdownMenuItem>
                      </ActionDropdown>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
};

export const Route = createFileRoute('/a/standalone-printers/')({
  component: StandalonePrintersPage,
});
